import {
  Alignment,
  Button,
  Classes,
  Navbar,
  Panel,
  PanelStack2,
} from "@blueprintjs/core";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import * as Yup from "yup";

import {
  BeneficiarySearchFormDto,
  BeneficiarySearchGroupedResultDto,
  EditBeneficiaryPersonDto,
  EmailDto,
  Forme,
  SearchBeneficiaryDto,
  TelephoneDto,
} from "../../../api";
import { useCommonHooks } from "../../../hooks";
import { useCrudApi } from "../../../hooks/useCrudApi";
import { ETLCodes } from "../../../locales";
import { SiteTheme } from "../../../theme";
import { bceNumberValidate } from "../../../utils/bceNumberValidate";
import { CustomFormGenerator } from "../../CustomFormGenerator";
import BeneficiarySelectorCiteriasPanel, {
  IBeneficiarySelectorCiteriasPanelProps,
} from "./BeneficiarySelectorCiteriasPanel";
import { IBeneficiarySelectorResultsPanelProps } from "./BeneficiarySelectorResultsPanel";

const Container = styled.div`
  height: 100%;
  & .beneficiary-selector-panel-stack {
    height: 100%;
    min-height: 90vh;
    width: 100%;
    & .${Classes.PANEL_STACK2_VIEW} {
      padding: 1px;
      background-color: transparent !important;
    }
  }
`;

const NavBarStyled = styled(Navbar)<{ theme: SiteTheme }>`
  position: relative;
  height: 50px !important;
  color: ${(props) => props.theme.mainTextColor} !important;
  box-shadow: none !important;
  margin-bottom: 1rem;

  & .${Classes.NAVBAR_HEADING} {
    color: ${(props) => props.theme.mainTextColor} !important;
    font-weight: 600;
  }

  & > .${Classes.NAVBAR_GROUP} {
    height: 50px;
  }
`;

export interface IBeneficiarySelectorProps {
  onSelected: (selected: BeneficiarySearchGroupedResultDto) => void;
  onClose: () => void;
  telephone?: string;
  completeForme?: { idpersonne: number; forme: Forme };
}

const buildFormesFromIds = (
  saved: SearchBeneficiaryDto
): BeneficiarySearchFormDto[] => {
  const formes: BeneficiarySearchFormDto[] = [];
  if (saved.idapprenant)
    formes.push(
      BeneficiarySearchFormDto.fromJS({
        forme: Forme.Apprenant,
        idapprenant: saved.idapprenant,
      })
    );
  if (saved.idformateur)
    formes.push(
      BeneficiarySearchFormDto.fromJS({
        forme: Forme.Formateur,
        idformateur: saved.idformateur,
      })
    );
  if (saved.idrepresentant)
    formes.push(
      BeneficiarySearchFormDto.fromJS({
        forme: Forme.Representant,
        idrepresentant: saved.idrepresentant,
      })
    );
  if (saved.idchefEntreprise)
    formes.push(
      BeneficiarySearchFormDto.fromJS({
        forme: Forme.ChefEntreprise,
        idchefEntreprise: saved.idchefEntreprise,
      })
    );
  if (saved.idtuteur)
    formes.push(
      BeneficiarySearchFormDto.fromJS({
        forme: Forme.Tuteur,
        idtuteur: saved.idtuteur,
      })
    );
  if (saved.idcontact)
    formes.push(
      BeneficiarySearchFormDto.fromJS({
        forme: Forme.Contact,
        idcontact: Number(saved.idcontact),
      })
    );
  return formes;
};

export const BeneficiarySelector: React.FunctionComponent<
  IBeneficiarySelectorProps
> = ({ onSelected, onClose, telephone, completeForme }) => {
  const { t, theme, apis } = useCommonHooks();

  const isEntreprise = React.useCallback(
    (forme: Forme) => [Forme.ChefEntreprise, Forme.Contact].includes(forme),
    []
  );

  const { data: typesTel } = useQuery(["types-tel-raw"], async () =>
    apis.referentials.phoneTypes()
  );
  const idtypeTelPerso = React.useMemo(
    () => typesTel?.find((tt) => tt.keyValue === "PERSO")?.idValue,
    [typesTel]
  );
  const idtypeTelGsmPerso = React.useMemo(
    () => typesTel?.find((tt) => tt.keyValue === "GSM_PERSO")?.idValue,
    [typesTel]
  );

  const { data: typesMail } = useQuery(["types-mail-raw"], async () =>
    apis.referentials.emailTypes()
  );
  const idtypeEmailPerso = React.useMemo(
    () => typesMail?.find((tt) => tt.keyValue === "PERSO")?.idValue,
    [typesMail]
  );

  // Full person loaded in completeForme mode. Kept so the save can preserve
  // fields the (partial) creation form does not expose, instead of wiping them.
  const fullPersonRef = React.useRef<EditBeneficiaryPersonDto>(undefined);

  const { data, validationErrors, onSave, isSaving } = useCrudApi({
    queryKey: ["beneficiary-suivi"],
    fetchFn: async () => {
      if (!completeForme) return SearchBeneficiaryDto.fromJS({ telephone });

      const [person, telTypes, mailTypes] = await Promise.all([
        apis.beneficiaryPersons.getBeneficiaryPerson(completeForme.idpersonne),
        apis.referentials.phoneTypes(),
        apis.referentials.emailTypes(),
      ]);
      fullPersonRef.current = person;
      const persoTelId = Number(
        telTypes.find((tt) => tt.keyValue === "PERSO")?.idValue
      );
      const gsmTelId = Number(
        telTypes.find((tt) => tt.keyValue === "GSM_PERSO")?.idValue
      );
      const persoMailId = Number(
        mailTypes.find((tt) => tt.keyValue === "PERSO")?.idValue
      );

      return SearchBeneficiaryDto.fromJS({
        idpersonne: person.idpersonne,
        nom: person.nom,
        prenom: person.prenom,
        dateNaissance: person.dateNaissance,
        codesexe: person.codesexe,
        nationalite: person.nationalite,
        registreNational: person.registreNational,
        forme: completeForme.forme,
        isBeneficiaryCreation: true,
        telephone: person.telephones?.find(
          (x) => x.idtypeTelephone === persoTelId
        )?.numero,
        gsm: person.telephones?.find((x) => x.idtypeTelephone === gsmTelId)
          ?.numero,
        email: person.emails?.find((x) => x.idtypeEmail === persoMailId)
          ?.adresseEmail,
        adresse: person.adresseDomicile,
      } as any);
    },
    saveFn: async (toSave: SearchBeneficiaryDto) => {
      const persoTelId = +idtypeTelPerso;
      const gsmTelId = +idtypeTelGsmPerso;
      const persoMailId = +idtypeEmailPerso;

      let dtoToSave: EditBeneficiaryPersonDto;
      if (completeForme && fullPersonRef.current) {
        // Adding a forme to an EXISTING person: the backend update branch remaps
        // every field, so we must resubmit the full person and only overlay the
        // fields edited in the form (identity, domicile address, perso/gsm/email,
        // siège/lieu). Other phones/emails, secondary addresses, bank accounts,
        // prenom2, national id... are preserved from the loaded person.
        const base = fullPersonRef.current;

        const existingPerso = base.telephones?.find(
          (x) => x.idtypeTelephone === persoTelId
        );
        const existingGsm = base.telephones?.find(
          (x) => x.idtypeTelephone === gsmTelId
        );
        const mergedPhones = (base.telephones ?? []).filter(
          (x) => x.idtypeTelephone !== persoTelId && x.idtypeTelephone !== gsmTelId
        );
        if (toSave.telephone)
          mergedPhones.push(
            TelephoneDto.fromJS({
              ...existingPerso,
              idpersonne: base.idpersonne,
              numero: toSave.telephone,
              idtypeTelephone: persoTelId,
            })
          );
        if (toSave.gsm)
          mergedPhones.push(
            TelephoneDto.fromJS({
              ...existingGsm,
              idpersonne: base.idpersonne,
              numero: toSave.gsm,
              idtypeTelephone: gsmTelId,
            })
          );

        const existingMail = base.emails?.find(
          (x) => x.idtypeEmail === persoMailId
        );
        const mergedEmails = (base.emails ?? []).filter(
          (x) => x.idtypeEmail !== persoMailId
        );
        if (toSave.email)
          mergedEmails.push(
            EmailDto.fromJS({
              ...existingMail,
              idpersonne: base.idpersonne,
              adresseEmail: toSave.email,
              idtypeEmail: persoMailId,
            })
          );

        dtoToSave = EditBeneficiaryPersonDto.fromJS({
          ...base,
          nom: toSave.nom,
          prenom: toSave.prenom,
          dateNaissance: toSave.dateNaissance,
          codesexe: toSave.codesexe,
          nationalite: toSave.nationalite,
          registreNational: toSave.registreNational,
          adresseDomicile: toSave.adresse,
          telephones: mergedPhones,
          emails: mergedEmails,
          idsiegeSocial: toSave.idsiegeSocial,
          numeroBce: toSave.numeroBce,
          denominationSociale: toSave.denominationSociale,
          idlieuFormation: toSave.idlieuFormation,
          nomLieuFormation: toSave.nomLieuFormation,
          formeFromSuivi: toSave.forme,
          idpersonne: toSave.idpersonne,
        });
      } else {
        // Creating a brand new person (idpersonne == 0): a partial DTO is safe.
        let personPhones = [];
        if (toSave.telephone)
          personPhones.push(
            TelephoneDto.fromJS({
              numero: toSave.telephone,
              idtypeTelephone: persoTelId,
            })
          );
        if (toSave.gsm)
          personPhones.push(
            TelephoneDto.fromJS({
              numero: toSave.gsm,
              idtypeTelephone: gsmTelId,
            })
          );

        let personEmails = [];
        if (toSave.email)
          personEmails.push(
            EmailDto.fromJS({
              adresseEmail: toSave.email,
              idtypeEmail: persoMailId,
            } as EmailDto)
          );

        dtoToSave = EditBeneficiaryPersonDto.fromJS({
          ...toSave,
          idpersonne: toSave.idpersonne,
          telephones: personPhones,
          emails: personEmails,
          adresseDomicile: toSave.adresse,
          formeFromSuivi: toSave.forme,
        });
      }

      const saved = await apis.beneficiaryPersons.saveBeneficiaryPerson(
        dtoToSave
      );
      return SearchBeneficiaryDto.fromJS({ ...toSave, ...saved });
    },
    onSaved: (saved: SearchBeneficiaryDto) => {
      if (completeForme) {
        onSelected(
          BeneficiarySearchGroupedResultDto.fromJS({
            idpersonne: saved.idpersonne,
            nom: saved.nom,
            prenom: saved.prenom,
            telephone: saved.telephone,
            gsm: saved.gsm,
            defaultForme: completeForme.forme,
            formes: buildFormesFromIds(saved),
          })
        );
        return;
      }
      onSelected(
        BeneficiarySearchGroupedResultDto.fromJS({
          idpersonne: saved.idpersonne,
          nom: saved.nom,
          prenom: saved.prenom,
          formes: [
            BeneficiarySearchFormDto.fromJS({
              forme: saved.forme,
              idapprenant: saved.idapprenant,
              idformateur: saved.idformateur,
              idrepresentant: saved.idrepresentant,
              idchefEntreprise: saved.idchefEntreprise,
              idtuteur: saved.idtuteur,
              idcontact: saved.idcontact,
            }),
          ],
        })
      );
    },
    serverValidationRootKey: "EditSuiviDto",
  });

  const initialPanel: Panel<
    | IBeneficiarySelectorCiteriasPanelProps
    | IBeneficiarySelectorResultsPanelProps
  > = {
    renderPanel: BeneficiarySelectorCiteriasPanel,
    props: {
      isEntreprise,
      onSelect: onSelected,
      onClose,
      isSaving,
      telephone,
      initialCreation: !!completeForme,
      completeForme,
    },
  };

  const [currentPanelStack, setCurrentPanelStack] = React.useState<
    Array<
      Panel<
        | IBeneficiarySelectorCiteriasPanelProps
        | IBeneficiarySelectorResultsPanelProps
      >
    >
  >([initialPanel]);

  const addToPanelStack = React.useCallback(
    (
      newPanel: Panel<
        | IBeneficiarySelectorCiteriasPanelProps
        | IBeneficiarySelectorResultsPanelProps
      >
    ) => setCurrentPanelStack((stack) => [...stack, newPanel]),
    []
  );
  const removeFromPanelStack = React.useCallback(
    () => setCurrentPanelStack(() => currentPanelStack.slice(0, -1)),
    [currentPanelStack]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape(
        {
          forme: Yup.string().when("isBeneficiaryCreation", {
            is: true,
            then: (schema) => schema.required(t(ETLCodes.Required)),
          }),
          numeroBce: Yup.string()
            .nullable()
            .test(
              "bce-validity",
              t(ETLCodes.InvalidBCE),
              (value) => !value || bceNumberValidate(value.toString())
            ),
          nom: Yup.string().when(["forme", "isBeneficiaryCreation"], {
            is: (forme, isBeneficiaryCreation) =>
              isBeneficiaryCreation && !isEntreprise(forme),
            then: (schema) => schema.required(t(ETLCodes.Required)),
          }),

          prenom: Yup.string().when(["forme", "isBeneficiaryCreation"], {
            is: (forme, isBeneficiaryCreation) =>
              isBeneficiaryCreation && !isEntreprise(forme),
            then: (schema) => schema.required(t(ETLCodes.Required)),
          }),
          gsm: Yup.string()
            .nullable()
            .when("isBeneficiaryCreation", {
              is: true,
              then: (schema) =>
                schema.test(
                  "gsm-required",
                  t(ETLCodes.AtLeastOnePhoneField),
                  function (value, context: any) {
                    return !!value || !!context.from[0].value.telephone;
                  }
                ),
            }),
          telephone: Yup.string()
            .nullable()
            .when("isBeneficiaryCreation", {
              is: true,
              then: (schema) =>
                schema.test(
                  "tel-required",
                  t(ETLCodes.AtLeastOnePhoneField),
                  function (value, context: any) {
                    return !!value || !!context.from[0].value.gsm;
                  }
                ),
            }),
          email: Yup.string().nullable(),
          idlieuFormation: Yup.number()
            .nullable()
            .when(
              [
                "forme",
                "isBeneficiaryCreation",
                "idsiegeSocial",
                "nomLieuFormation",
                "denominationSociale",
              ],
              {
                is: (
                  forme: Forme,
                  isBeneficiaryCreation: boolean,
                  idsiegeSocial: number,
                  nomLieuFormation: string,
                  denominationSociale: string
                ) =>
                  isBeneficiaryCreation &&
                  !nomLieuFormation &&
                  !!idsiegeSocial &&
                  forme === Forme.Tuteur,
                then: (schema) => schema.required(t(ETLCodes.Required)),
              }
            ),
          nomLieuFormation: Yup.string()
            .nullable()
            .when(
              [
                "forme",
                "isBeneficiaryCreation",
                "idsiegeSocial",
                "idlieuFormation",
                "denominationSociale",
              ],
              {
                is: (
                  forme: Forme,
                  isBeneficiaryCreation: boolean,
                  idsiegeSocial: number,
                  idlieuFormation: number,
                  denominationSociale: string
                ) =>
                  isBeneficiaryCreation &&
                  !idlieuFormation &&
                  !idsiegeSocial &&
                  !!denominationSociale &&
                  forme === Forme.Tuteur,
                then: (schema) => schema.required(t(ETLCodes.Required)),
              }
            ),
        },
        [
          ["gsm", "telephone"],
          ["idlieuFormation", "nomLieuFormation"],
        ]
      ),
    [isEntreprise, t]
  );

  return (
    <Container>
      <NavBarStyled theme={theme}>
        <Navbar.Group align={Alignment.LEFT}>
          <Navbar.Heading>{t(ETLCodes.SelectionBeneficiaire)}</Navbar.Heading>
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          <Button
            title={t(ETLCodes.Close)}
            icon="arrow-right"
            minimal={true}
            onClick={onClose}
          />
        </Navbar.Group>
      </NavBarStyled>
      <CustomFormGenerator
        initialValues={data}
        editable={true}
        editMode={true}
        saving={isSaving}
        validationSchema={validationSchema}
        validationErrors={validationErrors}
        onSubmit={onSave}
        preventDefaultSaveOnEnter
        deleteButtonPosition="none"
        saveButtonPosition="none"
        baseLeftPaddingPc={5}
        baseRightPaddingPc={5}
      >
        <PanelStack2
          className="beneficiary-selector-panel-stack"
          onOpen={addToPanelStack}
          onClose={removeFromPanelStack}
          renderActivePanelOnly={false}
          showPanelHeader={false}
          stack={currentPanelStack}
        />
      </CustomFormGenerator>
    </Container>
  );
};

export default BeneficiarySelector;
